const DEFAULT_OPTIONS = {
	sensitivity: 7,
	interval: 100,
	timeout: 0,
	handleFocus: false,
	useNotifications: false,
	preventHoverOnTouch: false,
}

class HoverIntent {
	constructor(el, onOver, onOut, options = {}) {
		this.el = el
		this.onOver = onOver
		this.onOut = onOut
		this.x = null
		this.y = null
		this.pX = null
		this.pY = null
		this.mouseOver = false
		this.focused = false
		this.h = {}
		this.state = 0
		this.timer = 0
		this.options = { ...DEFAULT_OPTIONS, ...options }

		this.bindEvent()

		if (this.el || !this.options.useNotifications) {
			this.el.addEventListener('mouseover', this.dispatchOverF, false)
			this.el.addEventListener('mouseout', this.dispatchOutF, false)
		}
		if (this.options.preventHoverOnTouch) {
			this.el.addEventListener('touchstart', () => {
				this.preventHover = true
			})
		}
	}

	bindEvent() {
		this.trackerF = (e) => {
			this.tracker(e)
		}
		this.dispatchOverF = (e) => {
			this.dispatchOver(e)
		}
		this.dispatchOutF = (e) => {
			this.dispatchOut(e)
		}
		this.dispatchFocusF = (e) => {
			this.dispatchFocus(e)
		}
		this.dispatchBlurF = (e) => {
			this.dispatchBlur(e)
		}
	}

	/**
	 * @description Resets the state to 0 (not hovered) and possibly fires onOut callback
	 * @private
	 * @memberof HoverIntent
	 */
	delay(e) {
		if (this.timer) this.timer = clearTimeout(this.timer)
		this.state = 0
		return this.focused ? undefined : this.onOut(e)
	}

	/**
	 * @description Constantly updates x and y form mousemove event
	 * @private
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	tracker(e) {
		this.x = e.clientX
		this.y = e.clientY
	}

	/**
	 * @description Compares mouse coordinates every <interval> ms to determine if the pointer is slowing down below the
	 * sensitivity. When this occurs, fires onOver callback
	 * @private
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	compare(e) {
		if (this.timer) this.timer = clearTimeout(this.timer)
		if (Math.abs(this.pX - this.x) + Math.abs(this.pY - this.y) < this.options.sensitivity) {
			this.state = 1
			return this.focused || this.preventHover ? undefined : this.onOver(e)
		}
		this.pX = this.x
		this.pY = this.y
		this.timer = setTimeout(() => {
			this.compare(e)
		}, this.options.interval)

		return undefined
	}

	/**
	 * @description Launched when the mouse enters the Element. Starts checking mouse speed calling compare()
	 * @public
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	dispatchOver(e) {
		this.mouseOver = true
		if (this.timer) this.timer = clearTimeout(this.timer)
		this.el.removeEventListener('mousemove', this.trackerF, false)

		if (this.state !== 1) {
			this.pX = e.clientX
			this.pY = e.clientY

			this.el.addEventListener('mousemove', this.trackerF, false)

			this.timer = setTimeout(() => {
				this.compare(e)
			}, this.options.interval)
		}
	}

	/**
	 * @description Launched when the mouse exits the Element.
	 * @public
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	dispatchOut(e) {
		this.mouseOver = false
		this.preventHover = false
		if (this.timer) this.timer = clearTimeout(this.timer)
		this.el.removeEventListener('mousemove', this.trackerF, false)

		if (this.state === 1) {
			this.timer = setTimeout(() => {
				this.delay(e)
			}, this.options.timeout)
		}
	}

	/**
	 * @description Handles onOver call back on focus
	 * @public
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	dispatchFocus(e) {
		if (!this.mouseOver) {
			this.focused = true
			this.onOver(e)
		}
	}

	/**
	 * @description Handles onOut call back on focus
	 * @public
	 * @memberof HoverIntent
	 * @param e mousemove event
	 */
	dispatchBlur(e) {
		if (!this.mouseOver && this.focused) {
			this.focused = false
			this.onOut(e)
		}
	}

	addFocus() {
		this.el.addEventListener('focus', this.dispatchFocusF, false)
		this.el.addEventListener('blur', this.dispatchBlurF, false)
	}

	removeFocus() {
		this.el.removeEventListener('focus', this.dispatchFocusF, false)
		this.el.removeEventListener('blur', this.dispatchBlurF, false)
	}

	remove() {
		if (!this.el) return
		this.el.removeEventListener('mouseover', this.dispatchOverF, false)
		this.el.removeEventListener('mouseout', this.dispatchOutF, false)
		this.removeFocus()
	}
}

export default HoverIntent
